// v19.8 (Feb 2026) — Helper tải video (VOD livestream, highlight, reel) dùng
// chung giữa TournamentVodSection + HighlightPlayerModal + các trang khác.
//
// Detect native app (`window.__pickleagueNativeApp === true`
// + `PickLeagueNative.downloadVod` bridge — có từ mobile v19.7):
//   → gọi bridge, save vào Camera Roll iOS / Movies Android, toast progress
//     realtime qua CustomEvent 'vod-download-status'.
// Web browser fallback: fetch blob → <a download> (giữ nguyên flow cũ).
//
// Usage:
//   import { downloadVideo } from "../../lib/downloadVideo";
//   downloadVideo({ url, filename, toast });
//
// Bug đã fix (v19.7): iOS WKWebView ignore attribute `download` trên
// blob URL → toast "Tải xong" giả nhưng file không lưu Photos.
import { toast as sonnerToast } from "sonner";

function sanitizeFilename(raw) {
  const clean = String(raw || "")
    .replace(/[^\p{L}\p{N}\s._-]/gu, "")
    .replace(/\s+/g, "_")
    .slice(0, 80);
  return clean || "video";
}

export async function downloadVideo({ url, filename, toast = sonnerToast, toastId = "video-dl" }) {
  if (!url) return;
  const safeName = sanitizeFilename(filename);

  // Native app: đẩy sang bridge, subscribe status → cập nhật toast realtime.
  if (typeof window !== "undefined"
      && window.__pickleagueNativeApp === true
      && window.PickLeagueNative?.downloadVod) {
    let unsub = null;
    try {
      toast.loading("Chuẩn bị tải…", { id: toastId });
      const handler = (ev) => {
        const d = ev.detail || {};
        if (d.state === "downloading") {
          const pct = Math.round((d.progress || 0) * 100);
          toast.loading(`Đang tải video… ${pct}%`, { id: toastId });
        } else if (d.state === "saving") {
          toast.loading("Đang lưu vào Ảnh…", { id: toastId });
        } else if (d.state === "done") {
          toast.success("Đã lưu vào Ảnh", {
            id: toastId,
            description: "Mở app Ảnh (iOS) hoặc Gallery (Android) để xem lại.",
            duration: 6000,
          });
          window.removeEventListener("vod-download-status", handler);
        } else if (d.state === "denied") {
          toast.error("Chưa được cấp quyền lưu Ảnh", {
            id: toastId,
            description: "Vào Cài đặt → PickLeague → Ảnh để bật quyền, rồi thử lại.",
            duration: 8000,
          });
          window.removeEventListener("vod-download-status", handler);
        } else if (d.state === "error") {
          toast.error(`Tải thất bại: ${d.error || "unknown"}`, { id: toastId });
          window.removeEventListener("vod-download-status", handler);
        }
      };
      window.addEventListener("vod-download-status", handler);
      unsub = () => window.removeEventListener("vod-download-status", handler);
      window.PickLeagueNative.downloadVod(url, safeName);
      // Safety: auto-unsub sau 15 phút (VOD lớn có thể lâu, tránh leak).
      setTimeout(() => { try { unsub?.(); } catch { /* noop */ } }, 15 * 60 * 1000);
    } catch (e) {
      try { unsub?.(); } catch { /* noop */ }
      toast.error(`Tải thất bại: ${e.message || e}`, { id: toastId });
    }
    return;
  }

  // Web browser: blob + <a download> (WKWebView ignore nhưng trên
  // Safari/Chrome desktop hoạt động bình thường).
  try {
    toast.loading("Đang tải video…", { id: toastId });
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${safeName}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    toast.success("Tải xong!", { id: toastId });
  } catch (e) {
    toast.error(`Tải thất bại: ${e.message || e}`, { id: toastId });
  }
}
